import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Activity, Loader2, ChevronRight, CheckCircle, XCircle, MinusCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_DOT = {
  sent: 'bg-emerald-400',
  failed: 'bg-rose-400',
  skipped: 'bg-amber-400',
  pending: 'bg-white/30',
};

const MatchIcon = ({ matched }) => {
  if (matched === true) return <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />;
  if (matched === false) return <XCircle className="w-3.5 h-3.5 text-white/30" />;
  return <MinusCircle className="w-3.5 h-3.5 text-white/30" />;
};

const formatRelative = (iso) => {
  if (!iso) return '—';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return `${Math.floor(hr / 24)}d ago`;
  } catch {
    return '—';
  }
};

const ActivityRow = ({ a }) => {
  const conf = a.confidence != null ? `${Math.round(a.confidence * 100)}%` : null;
  return (
    <Link
      to={`/calls/${a.call_id}`}
      className="block px-4 py-3 rounded-lg bg-white/5 border border-white/5 hover:border-teal-500/30 hover:bg-white/10 transition-colors"
      data-testid={`vf-activity-row-${a.id}`}
    >
      <div className="flex items-center gap-3">
        <MatchIcon matched={a.matched} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white text-sm font-medium truncate">{a.rule_name}</span>
            {a.action_type && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 capitalize">
                {a.action_type}
              </span>
            )}
            {conf && <span className="text-[10px] text-emerald-400/70">{conf}</span>}
          </div>
          <p className="text-white/40 text-xs mt-0.5 truncate">
            <span className="text-white/60">{a.agent_name || a.agent_id || 'unknown agent'}</span>
            {' · '}
            <span className="font-mono">{(a.call_id || '').slice(0, 14)}…</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[a.dispatch_status] || STATUS_DOT.pending}`} />
          <span className="text-white/40 text-xs capitalize">{a.dispatch_status || 'pending'}</span>
          <span className="text-white/20 text-xs">{formatRelative(a.created_at)}</span>
          <ChevronRight className="w-3.5 h-3.5 text-white/20" />
        </div>
      </div>
    </Link>
  );
};

export const RecentActivityPanel = ({ token, domainId = null }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      const p = new URLSearchParams({ limit: '25' });
      if (domainId) p.set('domain_id', domainId);
      axios.get(`${API}/admin/voicefabric/recent-activity?${p}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => { if (!cancelled) { setData(r.data?.activity || []); setLoading(false); } })
        .catch(() => { if (!cancelled) setLoading(false); });
    };
    load();
    const iv = setInterval(load, 30000); // refresh every 30s
    return () => { cancelled = true; clearInterval(iv); };
  }, [token, domainId]);

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-5 h-5 animate-spin text-white/30" />
      </div>
    );
  }

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid="vf-recent-activity">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-white/10">
        <Activity className="w-4 h-4 text-teal-400" />
        <h3 className="text-white text-sm font-semibold">Recent rule activity</h3>
        <span className="ml-auto text-xs text-white/30">{data.length} most recent · auto-refresh 30s</span>
      </div>
      <div className="p-2 space-y-1.5 max-h-[420px] overflow-y-auto">
        {data.length === 0 ? (
          <p className="text-white/30 text-sm py-8 text-center">
            No rule activity yet. Once calls are graded, matched rules will appear here.
          </p>
        ) : data.map(a => <ActivityRow key={a.id} a={a} />)}
      </div>
    </div>
  );
};
